import { useEffect } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { Dialog } from "../../../components/Dialog/Dialog.tsx";
import { TextField } from "../../../components/TextField/TextField.tsx";
import { trackEvent } from "../../../tracking/tracking.ts";
import { NEW_BLANK_PROJECT_PRESET } from "../../boardState/constants.ts";
import { useModels } from "../../hooks/useModels.ts";
import { useRedirectToBoard } from "../../hooks/useRedirectToBoard.ts";
import { useCreateBoard } from "../HomeIndex/hooks/useCreateBoard.ts";
import { QUICK_ACTIONS_CATEGORIES } from "./constants.ts";
import { QuickActionNavigation } from "./QuickActionNavigation.tsx";
import { QUICK_ACTIONS } from "./QuickActions.tsx";
import { QuickActionsButton } from "./QuickActionsButton.tsx";
import { quickActionsDialogStore } from "./stores/quickActionsDialogStore.ts";

export const QuickActionsDialog = () => {
  const { isOpen } = quickActionsDialogStore.useState();

  return (
    <Dialog
      title="Create"
      isOpen={isOpen ?? false}
      onOpenChange={() => {
        quickActionsDialogStore.close();
      }}
      content={<QuickActionsContent />}
    />
  );
};

const QuickActionsContent = () => {
  const selectedCategory = quickActionsDialogStore.useState(
    (state) => state.selectedCategory,
  );
  const searchText = quickActionsDialogStore.useState(
    (state) => state.searchText,
  );
  const filteredActionsPerSection =
    selectedCategory === null
      ? QUICK_ACTIONS_CATEGORIES.flatMap((category) =>
          QUICK_ACTIONS[category]
            .map((section) => ({
              section: section.section,
              subSection: category,
              quickActions: section.quickActions.filter((action) =>
                action.label.toLowerCase().includes(searchText.toLowerCase()),
              ),
            }))
            .filter(({ quickActions }) => quickActions.length > 0),
        )
      : QUICK_ACTIONS[selectedCategory].map((section) => ({
          section: section.section,
          subSection: undefined,
          quickActions: section.quickActions,
        }));
  const { models, isLoading } = useModels();

  const redirectToBoard = useRedirectToBoard();
  const { createBoard, isCreateBoardLoading } = useCreateBoard({
    onSuccess: (uuid) => {
      redirectToBoard({
        uuid,
        boardStatePreset: NEW_BLANK_PROJECT_PRESET,
      });
      quickActionsDialogStore.close();
    },
  });

  useEffect(() => {
    document.getElementById("quick-actions-dialog-scroll-container")?.scrollTo({
      top: 0,
    });
  }, [selectedCategory, searchText]);

  return (
    !isLoading && (
      <div className="flex-col w-[1000px] border-t">
        <div className="flex-row divide-x">
          <div className="flex-col gap-150 pt-200 bg-surface-secondary-rest">
            <div className="flex-col gap-150 pb-150 px-300 border-b">
              <TextField
                placeholder="Search..."
                size="md"
                value={searchText}
                onChange={(text) => {
                  quickActionsDialogStore.setSearchText(text.toLowerCase());
                }}
                onClick={() => {
                  quickActionsDialogStore.selectCategory(null);
                }}
                iconName="Search"
              />
              <Button
                variant="secondary"
                size="md"
                loading={isCreateBoardLoading}
                onClick={() => {
                  trackEvent("quick_actions:select", {
                    preset: NEW_BLANK_PROJECT_PRESET,
                  });
                  createBoard();
                }}
                iconName="Plus"
              >
                New project
              </Button>
            </div>
            <QuickActionNavigation />
          </div>
          <div
            id="quick-actions-dialog-scroll-container"
            className="flex-col h-[680px] flex-fill gap-200 px-400 pt-200 overflow-y-auto"
          >
            {searchText.length > 0 && selectedCategory === null && (
              <div className="text-secondary pt-200">
                <span className="text-secondary label-sm-default">
                  Search results for{" "}
                </span>
                <span className="text-primary label-sm-semibold">
                  {searchText}
                </span>
              </div>
            )}
            <div className="flex-col gap-200 py-200">
              {filteredActionsPerSection.map(
                ({ section, subSection, quickActions }) =>
                  quickActions.length > 0 && (
                    <div
                      key={`${section}-${subSection ?? ""}`}
                      className="flex-col gap-200"
                    >
                      <div className="heading-xl flex-row-item-center">
                        {section}
                        {subSection && (
                          <span className="label-lg-default text-secondary">
                            {` (${subSection})`}
                          </span>
                        )}
                      </div>
                      <div className="flex-row flex-fill overflow-x-auto gap-200 scrollbar-hide">
                        {quickActions.map((action) => (
                          <QuickActionsButton
                            key={action.label}
                            preset={action.preset}
                            label={action.label}
                            subLabel={action.subLabel}
                            childrenProp={action.childrenProp}
                            createAssetFromContentUuid={
                              action.createAssetFromContentUuid
                            }
                            models={models}
                          />
                        ))}
                      </div>
                    </div>
                  ),
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );
};
